import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import useProjectStore, { NO_PROJECT } from "../store/useProjectStore";
import "../styles/scoreDashboard.css";

/**
 * Per-agent SEO score history for one project.
 *
 * Every site-level agent keeps its own score. They are never averaged into a
 * single number — they measure different things, so "Technical SEO 84" and
 * "AI Search 41" are both true and a combined figure would mean nothing.
 *
 * Labels, thresholds and which agents are scored all come from the server
 * (GET /api/dashboard/contract and the dashboard payload). Nothing is
 * hard-coded here that the backend also defines, so the two cannot drift.
 */

/* ---------- formatting ---------- */

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const fmtShort = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      })
    : "";

/**
 * A delta chip. `null` is a real state, not a missing value: either this is
 * the first run, or the previous run used a different scoring method and is
 * not comparable. Both are said out loud rather than rendered blank.
 */
function DeltaTag({ delta }) {
  if (!delta || delta.value === null || delta.value === undefined) {
    return (
      <span className="sd-tag none">
        {delta && delta.blockedByMethodologyChange
          ? "method changed"
          : "first run"}
      </span>
    );
  }
  const cls =
    delta.direction === "up"
      ? "up"
      : delta.direction === "down"
        ? "down"
        : "flat";
  const arrow =
    delta.direction === "up" ? "▲" : delta.direction === "down" ? "▼" : "±";
  const sign = delta.value > 0 ? "+" : "";
  return (
    <span className={`sd-tag ${cls}`}>
      {arrow} {sign}
      {delta.value}
    </span>
  );
}

/* ---------- sparkline ---------- */

/**
 * Fixed 0-100 scale, so a row's shape is comparable with every other row.
 * Scaling each sparkline to its own range would make a 3-point wobble look
 * identical to a 40-point climb.
 */
function Sparkline({ agent }) {
  const pts = agent.segments
    .flatMap((s) => s.points)
    .filter((p) => p.score !== null);
  if (!pts.length) return null;

  const W = 58;
  const H = 18;
  const pad = 2;
  const x = (i) =>
    pts.length === 1 ? W / 2 : pad + (i * (W - pad * 2)) / (pts.length - 1);
  const y = (v) => pad + ((100 - v) * (H - pad * 2)) / 100;

  if (pts.length === 1) {
    return (
      <svg className="sd-spark" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        <circle
          cx={x(0)}
          cy={y(pts[0].score)}
          r="2.6"
          fill="var(--sd-accent)"
        />
      </svg>
    );
  }

  const d = pts
    .map(
      (p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(p.score).toFixed(1)}`,
    )
    .join(" ");
  return (
    <svg className="sd-spark" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <path
        d={d}
        fill="none"
        stroke="var(--sd-accent)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.8"
      />
      <circle
        cx={x(pts.length - 1)}
        cy={y(pts[pts.length - 1].score)}
        r="2.4"
        fill="var(--sd-accent)"
      />
    </svg>
  );
}

/* ---------- main chart ---------- */

function ScoreChart({ agent, noiseThreshold, onHover, onLeave }) {
  const all = agent.segments
    .flatMap((s) => s.points)
    .filter((p) => p.score !== null);
  if (!all.length) return null;

  const W = 720,
    H = 264,
    L = 40,
    R = 18,
    T = 26,
    B = 36;
  const n = all.length;
  const x = (i) =>
    n === 1 ? L + (W - L - R) / 2 : L + (i * (W - L - R)) / (n - 1);
  // Fixed 0-100 axis. Fitting the axis to the data turns a 1-point move into a
  // dramatic climb, which is the commonest way a score chart misleads.
  const y = (v) => T + ((100 - v) * (H - T - B)) / 100;

  const current = all[all.length - 1].score;
  const bandTop = y(Math.min(100, current + noiseThreshold));
  const bandBottom = y(Math.max(0, current - noiseThreshold));

  const gridlines = [0, 25, 50, 75, 100];
  const lastSegment = agent.segments.length - 1;

  let cursor = 0;
  const drawn = agent.segments.map((seg, si) => {
    const start = cursor;
    const valid = seg.points
      .map((p, k) => ({ p, i: start + k }))
      .filter(({ p }) => p.score !== null)
      .map(({ p, i }) => ({ p, cx: x(i), cy: y(p.score) }));
    cursor += seg.points.length;
    return { seg, si, valid, start };
  });

  return (
    <div className="sd-plot">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${agent.label} score history: ${all.map((p) => p.score).join(", ")}, on a fixed 0 to 100 axis.`}
      >
        {/* the range in which a change cannot be told apart from noise */}
        <rect
          x={L}
          y={bandTop}
          width={W - L - R}
          height={Math.max(0, bandBottom - bandTop)}
          fill="var(--sd-band)"
        />

        {gridlines.map((v) => (
          <g key={v}>
            <line
              x1={L}
              y1={y(v)}
              x2={W - R}
              y2={y(v)}
              stroke="var(--sd-grid)"
              strokeWidth="1"
            />
            <text className="sd-ax" x={L - 8} y={y(v) + 3.5} textAnchor="end">
              {v}
            </text>
          </g>
        ))}

        {drawn.map(({ seg, si, valid }) => {
          if (!valid.length) return null;
          const faded = si < lastSegment;
          return (
            <g key={seg.methodologyVersion + si}>
              {valid.length > 1 && (
                <path
                  d={valid
                    .map(
                      (c, k) =>
                        `${k ? "L" : "M"}${c.cx.toFixed(1)} ${c.cy.toFixed(1)}`,
                    )
                    .join(" ")}
                  fill="none"
                  stroke="var(--sd-accent)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={faded ? 0.42 : 1}
                />
              )}
              {valid.map((c, k) => {
                const isLatest = si === lastSegment && k === valid.length - 1;
                return (
                  <g key={c.p.executionId}>
                    <circle
                      cx={c.cx}
                      cy={c.cy}
                      r={isLatest ? 5.5 : 4}
                      fill="var(--sd-accent)"
                      stroke="var(--sd-surface)"
                      strokeWidth="2"
                      opacity={faded ? 0.5 : 1}
                    />
                    <circle
                      cx={c.cx}
                      cy={c.cy}
                      r="14"
                      fill="transparent"
                      style={{ cursor: "pointer" }}
                      onMouseEnter={(e) =>
                        onHover(e, {
                          score: c.p.score,
                          date: fmtDate(c.p.capturedAt),
                          method: seg.methodologyLabel,
                        })
                      }
                      onMouseMove={(e) =>
                        onHover(e, {
                          score: c.p.score,
                          date: fmtDate(c.p.capturedAt),
                          method: seg.methodologyLabel,
                        })
                      }
                      onMouseLeave={onLeave}
                    />
                    <text
                      className="sd-ax"
                      x={c.cx}
                      y={H - B + 18}
                      textAnchor="middle"
                    >
                      {fmtShort(c.p.capturedAt)}
                    </text>
                  </g>
                );
              })}
              {si === lastSegment && (
                <text
                  className="sd-ax"
                  x={valid[valid.length - 1].cx}
                  y={valid[valid.length - 1].cy - 12}
                  textAnchor="middle"
                  style={{
                    fill: "var(--sd-accent)",
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  {valid[valid.length - 1].p.score}
                </text>
              )}
            </g>
          );
        })}

        {/* A break is a scoring-method change, not missing data. The line is
            split rather than joined so the jump never reads as progress. */}
        {drawn.map(({ si, start }) => {
          if (si === 0) return null;
          const bx = (x(start - 1) + x(start)) / 2;
          return (
            <g key={`break-${si}`}>
              <line
                x1={bx}
                y1={T - 10}
                x2={bx}
                y2={H - B}
                stroke="var(--sd-line-strong)"
                strokeWidth="1"
                strokeDasharray="2 3"
              />
              <text className="sd-ax" x={bx + 5} y={T - 12}>
                scoring method changed
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ---------- the screen ---------- */

export default function ScoreDashboard({ projects, quota = null }) {
  const navigate = useNavigate();

  // `projects` is null while App.js is still fetching the list. That is a
  // different state from an empty array, which means the user genuinely has
  // none — showing "no projects yet" during the fetch would be wrong.
  const projectsLoading = projects === null || projects === undefined;
  const liveProjects = useMemo(
    () => (projects || []).filter((p) => p && p.id),
    [projects],
  );

  // The project comes from the header's ProjectSwitcher. This screen no
  // longer owns a picker, so it reads the one selection the whole app shares
  // and simply re-renders when the header changes it.
  const projectId = useProjectStore((s) => s.selectedProjectId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  // Which of the selected agent's targets is on screen: "site", or a page
  // path like "/pricing". Resets whenever the agent changes, because the
  // targets belong to the agent.
  const [selectedTargetKey, setSelectedTargetKey] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  // Bumped by "Try again" to re-run the fetch below for the same project.
  const [reloadKey, setReloadKey] = useState(0);

  // This screen does NOT correct the stored project itself. The header's
  // ProjectSwitcher is the single owner of that fallback, and it reads the
  // project list from the store while this screen gets it as a prop. When the
  // two lists disagree — which they do for a moment on Sign Out, where the
  // store is cleared before the app unmounts — two owners writing the same
  // value fight each other and React aborts with "Maximum update depth
  // exceeded". One owner, no fight.

  // A different project has its own agents with data, so the agent that was
  // open does not carry over.
  useEffect(() => {
    setSelectedAgentId(null);
  }, [projectId]);

  useEffect(() => {
    // "No project" is a valid choice elsewhere in the app but there is no
    // score history for it; the effect above swaps it for a real project, so
    // sit out this pass rather than requesting a dashboard for a sentinel.
    if (!projectId || projectId === NO_PROJECT) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .getProjectDashboard(projectId)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setSelectedAgentId((current) => {
          const list = res.data.agents || [];
          if (current && list.some((a) => a.agentId === current && a.hasData))
            return current;
          return (list.find((a) => a.hasData) || {}).agentId || null;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err.response?.data?.error ||
            "Could not load the score history for this project.",
        );
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, reloadKey]);

  const agents = data?.agents || [];
  const selectedAgent =
    agents.find((a) => a.agentId === selectedAgentId) || null;

  // Targets belong to the agent, so the selection follows it. An unknown key
  // (left over from the previously selected agent) falls back to that agent's
  // default rather than rendering nothing.
  const targets = selectedAgent?.targets || [];
  const activeTarget =
    targets.find((t) => t.key === selectedTargetKey) ||
    targets.find((t) => t.key === selectedAgent?.defaultTarget) ||
    targets[0] ||
    null;

  const openExecution = useCallback(
    (agentId, executionId) => {
      if (!executionId) return;
      navigate(
        `/agent/${agentId}?executionId=${encodeURIComponent(executionId)}&from=history&projectId=${projectId}`,
      );
    },
    [navigate, projectId],
  );

  const runAgent = useCallback(
    (agentId) => navigate(`/agent/${agentId}?projectId=${projectId}`),
    [navigate, projectId],
  );

  const onHover = useCallback((e, payload) => {
    setTooltip({ ...payload, x: e.clientX, y: e.clientY });
  }, []);
  const onLeave = useCallback(() => setTooltip(null), []);

  /* --- states that are not the dashboard --- */

  if (projectsLoading) {
    return (
      <div className="sd-root">
        <div className="sd-skel" style={{ height: 26, width: 200 }} />
        <div
          className="sd-card"
          style={{
            padding: 22,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div className="sd-skel" style={{ height: 22, width: 220 }} />
          <div className="sd-skel" style={{ height: 264 }} />
        </div>
      </div>
    );
  }

  if (!liveProjects.length) {
    return (
      <div className="sd-root">
        <div className="sd-card sd-state">
          <div className="sd-state-title">No projects yet</div>
          <div className="sd-state-body">
            A project locks one domain and up to two competitors. Create one,
            run an audit against it, and its scores will start building here.
          </div>
          {quota?.canCreate && (
            <button
              type="button"
              className="sd-btn primary"
              onClick={() => navigate("/settings")}
            >
              Create a project
            </button>
          )}
        </div>
      </div>
    );
  }

  // The header picks the project; until it has, this screen has nothing to
  // request. Same skeleton as the loading state — it lasts a single frame.
  if (!projectId || projectId === NO_PROJECT) {
    return (
      <div className="sd-root">
        <div className="sd-skel" style={{ height: 26, width: 200 }} />
        <div
          className="sd-card"
          style={{
            padding: 22,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div className="sd-skel" style={{ height: 22, width: 220 }} />
          <div className="sd-skel" style={{ height: 264 }} />
        </div>
      </div>
    );
  }

  const header = (
    <div className="sd-head">
      <div>
        <div className="sd-eyebrow">Score history</div>
        <h1>{data?.project?.name || "Overview"}</h1>
        <div className="sd-head-meta">
          {data?.project?.domain && (
            <span className="sd-chip">
              <i />
              {data.project.domain}
            </span>
          )}
          {data && (
            <span className="sd-chip">{data?.summary?.totalRuns} runs</span>
          )}
          {data?.summary?.lastRunAt && (
            <span className="sd-chip">
              last {fmtDate(data?.summary?.lastRunAt)}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  if (loading && !data) {
    return (
      <div className="sd-root">
        {header}
        <div
          className="sd-card"
          style={{
            padding: 22,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div className="sd-skel" style={{ height: 22, width: 220 }} />
          <div className="sd-skel" style={{ height: 264 }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sd-root">
        {header}
        <div className="sd-card sd-state">
          <div className="sd-state-title">Could not load scores</div>
          <div className="sd-state-body">{error}</div>
          <button
            type="button"
            className="sd-btn"
            onClick={() => setReloadKey((n) => n + 1)}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const noneHaveRun = data && data?.summary?.agentsWithData === 0;

  return (
    <div className="sd-root">
      {header}

      {noneHaveRun ? (
        <div className="sd-card sd-state">
          <div className="sd-state-title">No audits yet for this project</div>
          <div className="sd-state-body">
            Run any site-level agent against{" "}
            {data.project.domain || "this project"} and its score will appear
            here. A second run of the same agent starts the trend.
          </div>
          <button
            type="button"
            className="sd-btn primary"
            onClick={() => navigate("/agents")}
          >
            Browse agents
          </button>
        </div>
      ) : (
        <section className="sd-card sd-shell" aria-label="Agent score history">
          <div className="sd-list">
            <div className="sd-list-head">
              <span className="sd-eyebrow">Site-level agents</span>
              <span className="sd-eyebrow">
                {data?.summary?.agentsWithData} / {data?.summary?.agentsTotal}
              </span>
            </div>
            <div className="sd-list-body" role="tablist" aria-label="Agents">
              {agents.map((a) =>
                a.hasData ? (
                  <button
                    key={a.agentId}
                    type="button"
                    role="tab"
                    className="sd-row"
                    aria-selected={a.agentId === selectedAgentId}
                    onClick={() => {
                      setSelectedAgentId(a.agentId);
                      setSelectedTargetKey(null);
                    }}
                  >
                    <div className="sd-row-main">
                      <div className="sd-row-name">
                        {a.label}
                        {a.defaultTarget !== "site" && a.pageCount > 0 && (
                          <span className="sd-onpage">
                            {a.pageCount} page{a.pageCount === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>
                      <div className="sd-row-date">
                        {a.lastRunAt ? fmtDate(a.lastRunAt) : "—"}
                      </div>
                    </div>
                    <div className="sd-row-right">
                      <div className="sd-row-score">
                        {a.defaultTarget === "site" ? (
                          a.currentScore
                        ) : (
                          <span
                            className="sd-row-score-none"
                            aria-label="Page-level scores; open to choose a page"
                          ></span>
                        )}
                      </div>
                      <div className="sd-row-sub">
                        {/* A page score in this column would read as the
                            site's, so an agent with no whole-site run shows
                            what it has instead of a number. The score is in
                            the detail panel, where the page it belongs to is
                            named. */}
                        {a.defaultTarget === "site" ? (
                          <>
                            <DeltaTag delta={a.delta} />
                            <Sparkline agent={a} />
                          </>
                        ) : (
                          <span className="sd-tag none">page scores only</span>
                        )}
                      </div>
                    </div>
                  </button>
                ) : (
                  <div key={a.agentId} className="sd-row sd-empty">
                    <div className="sd-row-main">
                      <div className="sd-row-name">{a.label}</div>
                      <div className="sd-row-sub">
                        <button
                          type="button"
                          className="sd-run-link"
                          onClick={() => runAgent(a.agentId)}
                        >
                          Run this agent
                        </button>
                      </div>
                    </div>
                    {/* <div className="sd-row-score">—</div> */}
                  </div>
                ),
              )}
            </div>
          </div>

          <div className="sd-detail">
            {selectedAgent && activeTarget && (
              <>
                <div className="sd-detail-top">
                  <div>
                    <div className="sd-eyebrow">
                      {/* Scope first: without it a page score reads as the
                          site's, which is the one misreading that matters. */}
                      <span
                        className={
                          activeTarget.scope === "page"
                            ? "sd-scopetag page"
                            : "sd-scopetag site"
                        }
                      >
                        {activeTarget.scope === "page"
                          ? "Single page"
                          : "Whole site"}
                      </span>
                      <span className="sd__cat__data">
                        {activeTarget.methodologyLabel}
                        {" · "}
                        {activeTarget.scoreSource === "computed"
                          ? "computed from categories"
                          : "reported by agent"}
                      </span>
                    </div>
                    <div className="sd-detail-title">{selectedAgent.label}</div>
                    <div className="sd-detail-measures">
                      {selectedAgent.measures}
                    </div>
                    {(selectedAgent.targets.length > 1 ||
                      activeTarget.scope === "page") && (
                      <div className="sd-targetbar">
                        <span className="sd-eyebrow">Showing</span>
                        <select
                          value={activeTarget.key}
                          onChange={(e) => setSelectedTargetKey(e.target.value)}
                          aria-label={`Choose what to show for ${selectedAgent.label}`}
                        >
                          {selectedAgent.targets.map((t) => (
                            <option key={t.key} value={t.key}>
                              {t.label}
                              {`  ·  ${t.runs} run${t.runs === 1 ? "" : "s"}`}
                            </option>
                          ))}
                        </select>
                        {selectedAgent.scope === "site" &&
                          activeTarget.scope === "page" && (
                            <span className="sd-onpage">
                              site agent, run on this page
                            </span>
                          )}
                      </div>
                    )}
                  </div>
                  <div className="sd-detail-score">
                    <span className="sd-detail-num">
                      {activeTarget.currentScore}
                    </span>
                    <span className="sd-detail-den">/100</span>
                    <div className="sd-scorefor">
                      {activeTarget.scope === "page" ? (
                        <>
                          for{" "}
                          <strong>
                            {activeTarget.key === "/"
                              ? "/ (homepage)"
                              : activeTarget.key}
                          </strong>
                        </>
                      ) : (
                        <>
                          for <strong>the whole site</strong>
                        </>
                      )}
                    </div>
                    <div className="sd-detail-tags">
                      <span className="sd-eyebrow">vs prev</span>
                      <DeltaTag delta={activeTarget.delta} />
                      <span className="sd-eyebrow" style={{ marginLeft: 6 }}>
                        vs baseline
                      </span>
                      <DeltaTag delta={activeTarget.vsBaseline} />
                    </div>
                  </div>
                </div>

                <div className="sd-facts">
                  <div className="sd-fact">
                    <div className="sd-eyebrow">Runs</div>
                    <div className="sd-fact-v">{activeTarget.runs}</div>
                  </div>
                  <div className="sd-fact">
                    <div className="sd-eyebrow">Latest</div>
                    <div className="sd-fact-v">
                      {fmtDate(activeTarget.capturedAt)}
                    </div>
                  </div>
                  <div className="sd-fact">
                    <div className="sd-eyebrow">Previous</div>
                    <div className="sd-fact-v">
                      {fmtDate(activeTarget.delta.comparedTo)}
                    </div>
                  </div>
                  <div className="sd-fact">
                    <div className="sd-eyebrow">Method</div>
                    <div className="sd-fact-v">
                      {activeTarget.methodologyLabel}
                    </div>
                  </div>
                </div>

                <ScoreChart
                  agent={activeTarget}
                  noiseThreshold={data.scoreNoiseThreshold}
                  onHover={onHover}
                  onLeave={onLeave}
                />

                <div className="sd-links">
                  <button
                    type="button"
                    className="sd-link"
                    onClick={() =>
                      openExecution(
                        selectedAgent.agentId,
                        activeTarget.latestExecutionId,
                      )
                    }
                  >
                    <span className="k">Latest run</span> Open analysis →
                  </button>
                  <button
                    type="button"
                    className="sd-link"
                    disabled={!activeTarget.previousExecutionId}
                    onClick={() =>
                      openExecution(
                        selectedAgent.agentId,
                        activeTarget.previousExecutionId,
                      )
                    }
                  >
                    <span className="k">Previous run</span>
                    {activeTarget.previousExecutionId
                      ? " Open analysis →"
                      : " None yet"}
                  </button>
                </div>

                {activeTarget.delta.direction === "flat" &&
                  activeTarget.delta.value !== null && (
                    <div className="sd-note">
                      <i className="sd-note-bar" />
                      <div>
                        <b>
                          Movement of {Math.abs(activeTarget.delta.value)} point
                          {Math.abs(activeTarget.delta.value) === 1
                            ? ""
                            : "s"}{" "}
                          is not reported as progress.
                        </b>{" "}
                        Anything under {data.scoreNoiseThreshold} is inside what
                        re-running this agent produces on an unchanged site.
                      </div>
                    </div>
                  )}

                {activeTarget.delta.blockedByMethodologyChange && (
                  <div className="sd-note">
                    <i className="sd-note-bar" />
                    <div>
                      <b>No comparison to the previous run.</b> It used a
                      different scoring method, so the two numbers are not
                      measuring the same thing.
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      <div className="sd-note sd-standalone">
        <i className="sd-note-bar" style={{ background: "var(--sd-accent)" }} />
        <div>
          <b>Each agent keeps its own score.</b> They measure different things,
          so they are never averaged into one number.{" "}
          {activeTarget?.scope === "page" ? (
            <>
              You are looking at{" "}
              <strong>
                {activeTarget.key === "/" ? "the homepage" : activeTarget.key}
              </strong>{" "}
              only. A page score is tracked separately and never counts towards
              the whole-site score — one page is not the site.
            </>
          ) : (
            <>
              An agent run against a single page is kept under that page in its
              own dropdown, so one page can never move the whole-site score.
            </>
          )}
        </div>
      </div>

      {tooltip && (
        <div
          className="sd-tt"
          style={{
            opacity: 1,
            left: Math.min(tooltip.x + 14, window.innerWidth - 190),
            top: Math.max(8, tooltip.y - 62),
          }}
          role="status"
        >
          <div className="k">
            {tooltip.date} · {tooltip.method}
          </div>
          <div className="v">{tooltip.score} / 100</div>
        </div>
      )}
    </div>
  );
}
